/**
 * 知识库加载与查询：绑定字段建议的知识库。
 *
 * 知识库来源（内置静态 JSON 已废弃，src/data/binding-dict.json 已删除）：
 *   - 动态提取（高优先级）：从范例/用户报表 XML 经 parseReport → IR → mineFromIr 提取
 *   - 用户配置的 dictPath JSON（可选）：mine-dict 落盘的绑定知识库
 *
 * 查询优先级（自上而下）：
 *   1. patterns（标签+位置）→ 最精细
 *   2. labels（标签）→ 中等精度
 *   3. positions（位置）→ 粗粒度
 */

import type { loadUserReportBindings } from '../../scripts/load-user-reports.js';
import type { BindingDict, BindingDictEntry } from './mineFromIr.js';
import { mergeBindingDicts } from './binding-dict-merger.js';
import { getLogger } from '../logger.js';

const log = getLogger('core:template');

export type { BindingDict, BindingDictEntry } from './mineFromIr.js';

export interface BindingSuggestion {
  field: string;
  confidence: number;
  source: 'pattern' | 'label' | 'position' | 'userPattern';
}

/**
 * 知识库加载器与查询接口
 */
export class BindingDictLoader {
  private dict: BindingDict | null = null;
  private userDict: BindingDict | null = null;
  private aliasDict: BindingDict | null = null;
  private mergedDict: BindingDict | undefined = undefined;
  private path: string;

  constructor(dictPath: string) {
    this.path = dictPath;
  }

  /**
   * 打印加载成功日志（含知识库规模），便于在 LLM/终端输出中识别是否真正调用了 binding-dict.json
   */
  private logLoaded(label: string, dict: BindingDict): void {
    const { labels, positions, patterns, version } = dict;
    log.info(
      `📖 ${label}已加载 (labels=${Object.keys(labels).length}, positions=${Object.keys(positions).length}, patterns=${Object.keys(patterns).length}, version=${version})`,
    );
  }

  /**
   * 返回空知识库（无静态 JSON、无动态源时使用）。
   */
  private static emptyDict(): BindingDict {
    return {
      labels: {},
      positions: {},
      patterns: {},
      version: 0,
      miningDate: '',
      exampleCount: 0,
      bindingCount: 0,
    };
  }

  /**
   * 从文件加载知识库（同步）。
   * 未配置 dictPath（空字符串）时静默返回空知识库，不产生"加载失败"噪音。
   */
  loadSync(): BindingDict {
    if (this.dict) return this.dict;
    if (!this.path) {
      log.debug('未配置 bindings.dictPath，使用空知识库');
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }

    try {
      const fs = require('node:fs');
      const content = fs.readFileSync(this.path, 'utf-8');
      this.dict = JSON.parse(content) as BindingDict;
      this.logLoaded('绑定知识库', this.dict);
      return this.dict;
    } catch (error) {
      log.warn(`⚠️ 知识库加载失败 (${this.path}): ${(error as Error).message}`);
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }
  }

  /**
   * 从文件加载知识库（异步）。
   * 未配置 dictPath（空字符串）时静默返回空知识库。
   */
  async load(): Promise<BindingDict> {
    if (this.dict) return this.dict;
    if (!this.path) {
      log.debug('未配置 bindings.dictPath，使用空知识库');
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }

    try {
      const fs = await import('node:fs/promises');
      const content = await fs.readFile(this.path, 'utf-8');
      this.dict = JSON.parse(content) as BindingDict;
      this.logLoaded('绑定知识库', this.dict);
      return this.dict;
    } catch (error) {
      log.warn(`⚠️ 知识库加载失败 (${this.path}): ${(error as Error).message}`);
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }
  }

  /**
   * 动态加载用户报表知识库（用户已有报表 XML → parseReport → IR → 绑定知识）。
   */
  async loadUserReports(userReportsDir: string): Promise<BindingDict> {
    try {
      const { loadUserReportBindings } = await import(
        '../../scripts/load-user-reports.js'
      );
      const loaded = await loadUserReportBindings(userReportsDir);
      this.userDict = loaded;
      this.logLoaded('用户报表知识库', loaded);
      // 清除合并缓存
      this.mergedDict = undefined;
      return loaded;
    } catch (error) {
      log.warn(
        `⚠️ 用户报表加载失败 (${userReportsDir}): ${(error as Error).message}`,
      );
      this.userDict = BindingDictLoader.emptyDict();
      return this.userDict;
    }
  }

  /**
   * 动态加载范例/示例目录知识库（bindings.examplesDir → XML→IR→绑定知识）。
   * 与 loadUserReports 共享同一实现，语义上用于"从范例报表提取"。
   */
  async loadFromDynamic(examplesDir: string): Promise<BindingDict> {
    return this.loadUserReports(examplesDir);
  }

  /**
   * 加载数据集字段别名知识库（bindings.aliasDir → *.json 实时补充）。
   * 别名是用户明确定义的"标签/别名 → 字段"强映射，命中时由 query 锚定胜出，
   * 用于解决模板标签与范例 label 体系不一致导致的零命中问题。
   */
  async loadAliases(aliasDir: string): Promise<BindingDict> {
    try {
      const { loadFieldAliases } = await import(
        '../../scripts/load-field-aliases.js'
      );
      const loaded = await loadFieldAliases(aliasDir);
      this.aliasDict = loaded;
      this.logLoaded('字段别名', loaded);
      // 清除合并缓存，使别名并入合并知识库
      this.mergedDict = undefined;
      return loaded;
    } catch (error) {
      log.warn(
        `⚠️ 字段别名加载失败 (${aliasDir}): ${(error as Error).message}`,
      );
      this.aliasDict = BindingDictLoader.emptyDict();
      return this.aliasDict;
    }
  }

  /**
   * 获取合并的知识库（用户报表优先，字段别名作为最强来源补充）
   */
  getMergedDict(): BindingDict {
    if (this.mergedDict) return this.mergedDict;

    const builtinDict = this.loadSync();
    let merged = mergeBindingDicts(this.userDict ?? builtinDict, builtinDict, {
      userWeight: 1.3,
    });
    // 字段别名是用户明确定义的真值映射，作为最高优先级并入（权重最高）
    if (this.aliasDict) {
      merged = mergeBindingDicts(this.aliasDict, merged, {
        userWeight: 2.0, // 别名显著压过其他来源
        builtinWeight: 1.0,
      });
    }
    this.mergedDict = merged;
    this.logLoaded('合并知识库', merged);

    return merged;
  }

  /**
   * 查询：标签 + 位置 → 建议字段列表
   *
   * @param label 标签名（如 "金额"）
   * @param zone 位置纵向分类（如 "detail"）
   * @param side 位置横向分类（如 "center"）
   * @returns 排序后的建议列表（confidence 从高到低）
   */
  query(label?: string, zone?: string, side?: string): BindingSuggestion[] {
    // 如果加载了用户报表，使用合并知识库；否则使用内置知识库
    const dict = this.userDict ? this.getMergedDict() : this.loadSync();
    const suggestions: BindingSuggestion[] = [];

    // 优先级 -1：字段别名精确命中——别名是用户明确定义的「标签→字段」强真值，
    // 命中即锚定胜出（source:'userPattern'，置信度 1.0），直接返回不再降级查其他层。
    if (label && this.aliasDict) {
      const aliasEntry = this.aliasDict.labels[label];
      if (aliasEntry) {
        aliasEntry.fields.forEach((field, idx) => {
          if (!suggestions.some((s) => s.field === field)) {
            suggestions.push({
              field,
              confidence: aliasEntry.confidence[idx] ?? 1.0,
              source: 'userPattern',
            });
          }
        });
      }
      if (suggestions.length > 0) {
        log.debug(
          `字段别名「${label}」命中 ${suggestions.length} 条锚定建议`,
        );
        return suggestions;
      }
    }

    // 优先级 0：参考报表（userDict）精确绑定——只在同时命中「标签+位置」的 pattern 层时标记，
    // 供 scorer 将其锚定为高置信（直接胜出）。参考报表的绑定是明确真值（confidence 0.95），
    // 但合并后 dict 丢失来源信息，故单独对 userDict 查询，命中即标记 source:'userPattern'。
    if (label && zone && side && this.userDict) {
      const patternKey = `${label}@${zone}_${side}`;
      const userEntry = this.userDict.patterns[patternKey];
      if (userEntry) {
        userEntry.fields.forEach((field, idx) => {
          suggestions.push({
            field,
            confidence: userEntry.confidence[idx] ?? 0.95,
            source: 'userPattern',
          });
        });
      }
    }

    // 优先级 1：pattern（标签+位置）——对已由 userPattern 命中的字段去重，避免重复建议
    if (label && zone && side) {
      const patternKey = `${label}@${zone}_${side}`;
      const entry = dict.patterns[patternKey];
      if (entry) {
        entry.fields.forEach((field, idx) => {
          if (suggestions.some((s) => s.field === field)) return;
          suggestions.push({
            field,
            confidence: entry.confidence[idx] ?? 0.5,
            source: 'pattern',
          });
        });
      }
    }

    // 优先级 2：label（标签）
    if (label) {
      const entry = dict.labels[label];
      if (entry) {
        entry.fields.forEach((field, idx) => {
          if (!suggestions.some((s) => s.field === field)) {
            suggestions.push({
              field,
              confidence: (entry.confidence[idx] ?? 0.5) * 0.8, // 降权
              source: 'label',
            });
          }
        });
      }
    }

    // 优先级 3：position（位置）
    if (zone && side) {
      const posKey = `${zone}_${side}`;
      const entry = dict.positions[posKey];
      if (entry) {
        entry.fields.forEach((field, idx) => {
          if (!suggestions.some((s) => s.field === field)) {
            suggestions.push({
              field,
              confidence: (entry.confidence[idx] ?? 0.5) * 0.6, // 降权
              source: 'position',
            });
          }
        });
      }
    }

    // 排序：confidence 从高到低
    suggestions.sort((a, b) => b.confidence - a.confidence);

    if (suggestions.length > 0) {
      log.debug(
        `标签「${label ?? ''}」@${zone ?? ''}_${side ?? ''} 命中 ${suggestions.length} 条知识库建议（${suggestions
          .slice(0, 3)
          .map((s) => `${s.field}:${(s.confidence * 100).toFixed(0)}%`)
          .join(', ')}）`,
      );
    }

    return suggestions;
  }

  /**
   * 查询：仅按标签
   */
  queryByLabel(label: string): BindingSuggestion[] {
    return this.query(label);
  }

  /**
   * 查询：仅按位置
   */
  queryByPosition(zone: string, side: string): BindingSuggestion[] {
    return this.query(undefined, zone, side);
  }

  /**
   * 获取知识库统计信息
   */
  getStats(): {
    labelCount: number;
    positionCount: number;
    patternCount: number;
    totalBindings: number;
    version: number;
    miningDate: string;
    hasUserReports: boolean;
  } {
    const dict = this.userDict ? this.getMergedDict() : this.loadSync();
    return {
      labelCount: Object.keys(dict.labels).length,
      positionCount: Object.keys(dict.positions).length,
      patternCount: Object.keys(dict.patterns).length,
      totalBindings: dict.bindingCount || 0,
      version: dict.version,
      miningDate: dict.miningDate,
      hasUserReports: !!this.userDict,
    };
  }

  /**
   * 获取原始知识库对象
   */
  getDict(): BindingDict {
    return this.userDict ? this.getMergedDict() : this.loadSync();
  }
}

/**
 * 全局单例实例（默认路径）
 */
let globalLoader: BindingDictLoader | null = null;

/**
 * 获取全局加载器实例。
 *
 * 注意：内置静态知识库已废弃（src/data/binding-dict.json 已删除），
 * 知识库完全来自动态提取（examplesDir）或用户配置的 dictPath；
 * 默认空字符串表示不加载任何静态 JSON，直接返回空知识库。
 */
export function getBindingDictLoader(dictPath: string = ''): BindingDictLoader {
  if (!globalLoader) {
    globalLoader = new BindingDictLoader(dictPath);
  }
  return globalLoader;
}

/**
 * 快捷查询函数（使用全局加载器）
 */
export function suggestBindings(
  label?: string,
  zone?: string,
  side?: string,
  dictPath?: string,
): BindingSuggestion[] {
  const loader = getBindingDictLoader(dictPath);
  return loader.query(label, zone, side);
}
